import React from 'react';
import { cn } from '@/shadcn/lib/utils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/shadcn/components/ui/tooltip';
import { PROFILE_CONSTANTS } from './constants';

export interface ProfileNameRowProps {
  icon: React.ReactNode;
  name: string;
  fallback?: string;
  className?: string;
}

/**
 * ProfileNameRow
 * --------------
 * A specialized row component for displaying creator names with ellipsis truncation
 * and tooltip functionality. Shows the full name in a tooltip when the text is truncated.
 *
 * Features:
 * - CSS-based ellipsis truncation for long names
 * - Tooltip showing full name on hover when truncated
 * - Fallback text when name is not available
 * - Consistent styling with other profile metadata rows
 * - Uses Social application's consistent tooltip styling
 * - Proper space consideration to the right of truncation
 */
const ProfileNameRow: React.FC<ProfileNameRowProps> = ({
  icon,
  name,
  fallback = PROFILE_CONSTANTS.DEFAULT_FALLBACK_TEXT,
  className
}) => {
  const displayName = name || fallback;
  const shouldShowTooltip = name && name.length > PROFILE_CONSTANTS.TOOLTIP_CHAR_THRESHOLD; // Show tooltip for names longer than threshold

  return (
    <div className={cn('text-xs text-gray-500 flex items-center gap-1', className)}>
      {icon}
      {shouldShowTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={cn('truncate min-w-0 flex-1', PROFILE_CONSTANTS.DEFAULT_MAX_WIDTH)}>
              {displayName}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            <div className="text-xs font-normal">
              {displayName}
            </div>
          </TooltipContent>
        </Tooltip>
      ) : (
        <span className={cn('truncate min-w-0 flex-1', PROFILE_CONSTANTS.DEFAULT_MAX_WIDTH)}>
          {displayName}
        </span>
      )}
    </div>
  );
};

export { ProfileNameRow };
